import { CEL } from './styles/cel';
import { GEO } from './styles/geo';
import { ION } from './styles/ion';
/* ============================================================
   武器特效的風格檔（候選比較用）
   ============================================================

   使用者 2026-08-05 第四次定調：第三版（實彈／軍械）四項全否 ——
   **太糊沒有形狀、風格不合 low poly、打擊感不足、沒有記憶點**。
   所以這一輪不是調參數，是換整個視覺語言，並且**同時做三個候選**
   在 `/vfx-preview.html` 並排比較。

   ## 為什麼要有「風格檔」這一層

   前三次翻案的成本都花在「把整套配方改寫一遍、發現不對、再改寫一遍」。
   這一層把「配方」與「風格」分開：`weapon-fx.ts` 仍然是唯一的呼叫入口
   （模擬層完全不知道有這回事），入口內部問一句「現在是哪個風格」再分派。

   代價是一個 if；換來的是 **preview 頁與遊戲跑的是同一份程式碼** ——
   前三版被否掉的一個共同原因是「看樣張覺得可以、進遊戲不是那回事」，
   而樣張與遊戲之間只要有第二份實作，那個落差就永遠存在。

   ## 四個風格

   | id | 名稱 | 一句話 |
   |---|---|---|
   | `v3` | 實彈／軍械 | 現行版本，留著當對照組 |
   | `geo` | 硬邊幾何衝擊 | 不透明的多邊形與三角碎片，跟場景同一種語言 |
   | `cel` | 賽璐珞拍 | 三張手繪剪影逐格播放，打擊感來自幀不是光 |
   | `ion` | 硬邊能量 | 冷色，但每一片都有銳利邊界（有邊界就不是魔法） |

   ⚠️ `v3` 的實作留在 `weapon-fx.ts` 原地（`muzzle`/`impact` 欄位為
   `undefined` 就是「走內建那一套」）。等使用者挑定，落選的三個連同這一層
   一起收掉，只留下勝出的那一套配方。 */

/** 一個風格的四個語意色。**只有四個**是刻意的限制 ——
    第三版有五個顏色而且色相全部相鄰（橙、黃橙、灰褐…），
    結果是任何一招都讀成同一團暖色。 */
export type Cols = {
  /** 白熱核心：每一招最前面那一格。所有風格都是接近白的。 */
  hot: number;
  /** 招牌色。**這是三個風格之間最大的差別**，也是玩家的識別色。 */
  acc: number;
  /** 描邊／陰影。賽璐珞的黑邊、幾何的暗面。 */
  ink: number;
  /** 碎屑：被打下來的金屬。中性灰，三個風格共用。 */
  chip: number;
};

export type WStyle = {
  id: string;
  name: string;
  tag: string;
  c: Cols;
  /** 這個風格要求的泛光。**泛光是風格的一部分**（使用者拍板）：
      不靠泛光的方向可以把全域還原到場景原本調好的 1.15/0.66，
      場景的火光與窗燈就會回來。 */
  bloom: { strength: number; radius: number; threshold: number };
  /** 演出層。粒子只負責一半的打擊感，另一半在這裡。
      `shake` 是主砲單發的震幅、`critStop` 是暴擊的頓幀秒數、
      `camPush` 是後座推鏡。 */
  punch: { shake: number; critStop: number; camPush: number };
  /** 曳光彈的長相。主砲每秒 3.75 發，彈體在畫面上的時間比任何特效都長 ——
      它其實是主武器「質感」的最大宗，而現行版本是一根白色方棒。 */
  bullet: { c: number; w: number; len: number; ink?: number };
  /* 以下每一個都可省略，省略 = 走 `weapon-fx.ts` 的內建實作（現行 v3）。
     所以一個風格可以只覆寫主武器，其餘沿用 —— 候選階段就是這樣做的
     （B 與 D 只做了主武器，A 選定之後才補齊）。 */
  /** 主武器：槍口。`power` 0.35（主砲連射）～1.0（大招）。 */
  muzzle?: (x: number, y: number, z: number, ang: number, power: number) => void;
  /** 主武器：命中（含暴擊）。 */
  impact?: (x: number, y: number, z: number, ang: number, crit: boolean) => void;
  /** 副武器：飛彈／地雷爆炸。`r` 是爆炸半徑。 */
  explode?: (x: number, y: number, z: number, c: number, r: number, power: number) => void;
  /** 副武器：齊射離架的那一拍。 */
  launch?: (x: number, y: number, z: number, ang: number, c: number) => void;
  /** 大招：發射的那一拍。 */
  ultFire?: (x: number, y: number, z: number, ang: number, c: number) => void;
  /** 大招：光束掃到東西的落點（每 0.07 秒一次，要最便宜）。 */
  beamHit?: (x: number, y: number, z: number, c: number) => void;
  /** 掛載：開火那一拍。 */
  gadgetFire?: (id: string, x: number, y: number, z: number, ang: number) => void;
  /** 掛載：軌道光柱落地（全遊戲最重的一擊）。 */
  orbitalHit?: (x: number, z: number, r: number, c: number) => void;
  /** 地面衝擊環。**很多掛載與小招都在用它**，沒覆寫的話畫面上會冒出
      第三版的柔邊揚塵 —— 一個風格漏掉一個鉤子，整套語言就破一個洞。 */
  shockRing?: (x: number, z: number, c: number, r0: number, r1: number, t: number, op?: number) => void;
  /** 近戰／範圍：斬擊弧。 */
  slashArc?: (x: number, y: number, z: number, yaw: number, range: number, c: number) => void;
  /** 飛彈排氣（沿飛行路徑一路留下）。 */
  exhaust?: (x: number, y: number, z: number, scale: number) => void;
};

/** 現行版本。只有數值沒有實作 —— 配方在 `weapon-fx.ts` 原地。
    數值照現況抄：`postfx.ts` 的 BLOOM、`sim/weapons.ts:76` 的 shake 0.012、
    `CAM.push += 0.16`、`world/pools.ts` 的白色方棒彈體 0.16×0.16×1.5。 */
export var V3: WStyle = {
  id: 'v3', name: '實彈／軍械（現行）', tag: '對照組：柔邊火光 + 煙塵量體',
  c: { hot: 0xffe7bd, acc: 0xff8f3a, ink: 0x3a3630, chip: 0x8d8a84 },
  bloom: { strength: 0.62, radius: 0.6, threshold: 0.74 },
  punch: { shake: 0.012, critStop: 0, camPush: 0.16 },
  bullet: { c: 0xffffff, w: 0.16, len: 1.5 }
};

export var STYLES: WStyle[] = [V3, GEO, CEL, ION];

/** 每個風格原本的色（色彩鎖定要還原用）。 */
var ORIG: Record<string, Cols> = {};
for (var i = 0; i < STYLES.length; i++) ORIG[STYLES[i].id] = STYLES[i].c;

/* **預設風格 = A 硬邊幾何**（使用者 2026-08-05 選定）。
   在這之前預設是 `V3`，那是候選比較期間的安排：讓遊戲維持原樣、
   只有 `/vfx-preview.html` 會切。現在方向定了，遊戲跟著走同一套。 */
var active: WStyle = GEO;

export function activeStyle() { return active; }
export function styleOf(id: string) {
  for (var i = 0; i < STYLES.length; i++) if (STYLES[i].id === id) return STYLES[i];
  return null;
}
export function setStyle(id: string) {
  var s = styleOf(id);
  if (s) active = s;
  return active;
}

/** 色彩鎖定：把所有風格切成同一組色。

    使用者選了「三個版本各用不同色」，那是對的 —— 顏色本來就是風格的一部分。
    但比較的時候會遇到一個老問題：**你分不出自己喜歡的是形狀還是顏色。**
    所以留一個開關，一鍵把變因收成單一個。 */
export function lockColors(on: boolean) {
  for (var i = 0; i < STYLES.length; i++) {
    STYLES[i].c = on ? GEO.c : ORIG[STYLES[i].id];
  }
}
